import React, { useCallback, useEffect, useState } from "react";
import { HiOutlineDocumentDuplicate, HiOutlineLightBulb, HiOutlineThumbUp, HiOutlineThumbDown } from "react-icons/hi";
import { HiArrowPath } from "react-icons/hi2";
import { Chat, Message, ResearchLink } from "./types";
import { useDispatch, useSelector } from 'react-redux';
import useChatService from '../../services/chat';
import { extractLinksFromResearch } from './TimelineThinking/ResearchPills';
import SelectionAddToChat from './SelectionAddToChat';
import { setResearchLinks, setShowSourcesPanel, editLastUserMessage } from "../../redux/slices/chat";
import { PMRenderer } from "./PMRenderer";
import { INTERNAL_LINK_CLASSES, INTERNAL_LINK_ICON_CLASSES } from "./PMRenderer/InternalLink";
import MessageSimulationDiff from "./MessageSimulationDiff";
import ChatMessageTimeline from "./TimelineThinking/ChatMessageTimeline";
import MessageInterrupt from "./MessageInterrupt";
import UserMemoryManageModal from "../UserMemoryManageModal/UserMemoryManageModal";
import MemorySavedHeader from "./MemorySavedHeader";

interface MemoizedBotMessageProps {
  index: number;
  currentChat: Chat;
  inputTextRef: React.RefObject<HTMLTextAreaElement>;
  message: Message;
  isLastBotMessage: boolean;
  canAddSelection: boolean;
  scrollToUserMessage: () => void;
  isSSEConnected: boolean;
  respondWithUserMessage: (
    responseType: 'answer' | 'modify' | 'approve' | 'cancel',
    answerText: string,
    opts?: {
      interruptId?: string;
      chatId?: string | number;
      messageId?: number | null;
    }
  ) => void;
  handleInterruptResponse: (
    responseType: string,
    responseData: any,
    interruptId?: string,
    chatId?: string | number,
    messageId?: number | null
  ) => void;
  sendMessage: (messageText: string, editMessageId?: number | null) => Promise<void>;
  resetExpansion?: number;
  canViewMemoryTags?: boolean;
}

const MemoizedBotMessage: React.FC<MemoizedBotMessageProps> = ({
  index,
  message,
  inputTextRef,
  currentChat,
  isLastBotMessage,
  scrollToUserMessage,
  canAddSelection,
  isSSEConnected,
  respondWithUserMessage,
  handleInterruptResponse,
  sendMessage,
  resetExpansion = 0,
  canViewMemoryTags = true,
}) => {
  const dispatch = useDispatch();
  const { sendMessageFeedback } = useChatService();

  // Determine if this message is an error based on isError flag OR tags
  const errorTag = (message.tags || []).find(t => ['error', 'google_error'].includes(t.type));
  const isEffectiveError = message.isError || !!errorTag;
  const effectiveErrorText = isEffectiveError ? (message.text || errorTag?.content || '') : '';

  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const [showMemoryModal, setShowMemoryModal] = useState(false);

  const feedbackLoading = useSelector((state: any) =>
    message.messageId ? (state.chatMessages.feedbackLoading?.[message.messageId] || false) : false
  );
  const feedbackStatus = useSelector((state: any) =>
    message.messageId ? state.chatMessages.feedbackStatus?.[message.messageId] : undefined
  );

  const {
    showBigChatbox,
    interruptLoading
  } = useSelector((state: any) => state.chatMessages);

  // Show loading shimmer when message is loading and either has no content OR streaming hasn't started
  const hasContent = message.text || (message.tags && message.tags.length > 0);
  const showSpinner = currentChat?.isLoading && (!hasContent || !currentChat?.isStreaming);

  // Keep avatar spinning while streaming or initially loading
  const shouldSpinAvatar = showSpinner || currentChat?.isStreaming;

  // Helper function to format list content for copying
  const formatListContent = (content: string) => {
    if (!content || !content.trim()) return '';

    // Split into lines and clean up
    const lines = content.split('\n')
      .map((line: string) => line.trim())
      .filter((line: string) => line.length > 0);

    if (lines.length === 0) return '';

    // Check if content already has bullet points or numbers
    const hasBullets = lines.some((line: string) => line.match(/^[•\-\*]\s+/));
    const hasNumbers = lines.some((line: string) => line.match(/^\d+\.?\s+/));

    if (hasBullets || hasNumbers) {
      // Already formatted, just clean up spacing
      return lines
        .map((line: string) => {
          // Normalize bullet points to consistent format
          line = line.replace(/^[•\-\*]\s+/, '• ');
          // Normalize numbered lists
          line = line.replace(/^(\d+)\.?\s+/, '$1. ');
          return line;
        })
        .join('\n');
    }

    // If no formatting detected, try to determine if it should be numbered or bulleted
    // Use numbered list if content suggests steps or order
    const suggestsOrder = content.toLowerCase().includes('step') ||
      content.toLowerCase().includes('first') ||
      content.toLowerCase().includes('then') ||
      content.toLowerCase().includes('next') ||
      content.toLowerCase().includes('finally');

    if (suggestsOrder && lines.length > 1) {
      // Create numbered list
      return lines
        .map((line: string, index: number) => `${index + 1}. ${line}`)
        .join('\n');
    } else {
      // Create bullet list
      return lines
        .map((line: string) => `• ${line}`)
        .join('\n');
    }
  };

  const handleAddSelectionToInput = useCallback((selected: string) => {
    if (!selected || !canAddSelection) return;

    const text = selected.trim();
    if (!text) return;

    const textarea = inputTextRef.current as HTMLTextAreaElement | null;

    // Read the current value from the shared textarea ref (single instance across app)
    const currentValue = textarea?.value || '';
    let next = '';

    if (textarea && typeof textarea.selectionStart === 'number' && typeof textarea.selectionEnd === 'number') {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const before = currentValue.slice(0, start);
      const after = currentValue.slice(end);
      const spacerBefore = before && !/\s$/.test(before) ? ' ' : '';
      const spacerAfter = after && !/^\s/.test(after) ? ' ' : '';
      next = `${before}${spacerBefore}${text}${spacerAfter}${after}`.replace(/\s{3,}/g, '  ');
    } else {
      const spacer = currentValue && !/\s$/.test(currentValue) ? ' ' : '';
      next = `${currentValue}${spacer}${text}`;
    }

    try {
      window.dispatchEvent(new CustomEvent('cadence:chat-prefill', {
        detail: {
          fullPrompt: next,
          dontShrink: true,
          source: 'bot-message'
        }
      }));
    } catch { }

    setTimeout(() => {
      try { textarea?.focus(); } catch { }
    }, 0);
  }, [canAddSelection, inputTextRef]);

  const researchLinks: ResearchLink[] = React.useMemo(() => {
    if (!message?.tags || message.tags.length === 0) return [];
    const out: ResearchLink[] = [];
    for (const tag of message.tags) {
      if (!tag || typeof tag !== 'object') continue;
      if (tag.type !== 'research') continue;
      const content = tag.content || '';
      out.push(...extractLinksFromResearch(content));
    }

    const seen = new Set<string>();
    const deduped: ResearchLink[] = [];
    for (const l of out) {
      const key = l.host || l.url;
      if (key && !seen.has(key)) {
        seen.add(key);
        deduped.push(l);
      }
    }

    return deduped;
  }, [message?.tags]);

  const updateResearchLinks = () => {
    dispatch(setShowSourcesPanel(true));
    dispatch(setResearchLinks(researchLinks));
  }

  const sourceIcons: string[] = React.useMemo(() => {
    const icons: string[] = [];
    for (const l of researchLinks) {
      let domain = l.host;
      if (!domain) {
        try { domain = new URL(l.url).hostname; } catch { }
      }
      const src = l.favicon || (domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=64` : '');
      if (src && !icons.includes(src)) icons.push(src);
    }
    return icons.slice(0, 5);
  }, [researchLinks]);

  const handleCopyBotMessage = async () => {
    try {
      // For bot messages, extract clean text content
      let textToCopy = message.text || '';

      // Remove XML tags (thinking, progress, etc.)
      textToCopy = textToCopy.replace(/<(thinking|progress|transfer|research)>[\s\S]*?<\/\1>/g, '').trim();
      textToCopy = textToCopy.replace(/<(thinking|progress|transfer|research)>[\s\S]*$/g, '').trim();

      // Process PM tags and format lists properly
      textToCopy = textToCopy.replace(/<pm:(card|status|progress|list|table)[^>]*>([\s\S]*?)<\/pm:\1>/g, (match, tagType, content) => {
        if (tagType === 'list') {
          // Format list content with proper numbering/bullets
          return formatListContent(content);
        }
        // For PM tags, just return the content
        return content;
      }).trim();

      // Remove any remaining opening PM tags without closing tags (for incomplete streaming)
      textToCopy = textToCopy.replace(/<pm:(card|status|progress|list|table)[^>]*>/g, '').trim();

      // Clean up any remaining markdown or formatting for plain text copy
      textToCopy = textToCopy.replace(/\*\*(.*?)\*\*/g, '$1'); // Bold
      textToCopy = textToCopy.replace(/\*(.*?)\*/g, '$1'); // Italic
      textToCopy = textToCopy.replace(/`(.*?)`/g, '$1'); // Inline code
      textToCopy = textToCopy.replace(/```[\s\S]*?```/g, ''); // Code blocks
      textToCopy = textToCopy.replace(/#+\s*/g, ''); // Headers
      textToCopy = textToCopy.replace(/[-*+]\s+/g, ''); // List items
      textToCopy = textToCopy.replace(/\n\s*\n/g, '\n'); // Multiple newlines
      textToCopy = textToCopy.trim();

      await navigator.clipboard.writeText(textToCopy);

      // Show success indication
      const copyButton = document.querySelector(`[data-bot-message-id="${message.id || 'temp'}"] .copy-button`);
      if (copyButton) {
        const originalContent = copyButton.innerHTML;
        copyButton.innerHTML = '<svg class="w-3 h-3 sm:w-4 sm:h-4 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path></svg>';
        setTimeout(() => {
          copyButton.innerHTML = originalContent;
        }, 1000);
      }
    } catch (err) {
      console.error('Failed to copy bot message:', err);
    }
  };

  const handleThumbsUpBotMessage = async () => {
    if (!currentChat?.chatId || !message.messageId) return;
    dispatch({ type: 'chatMessage/setMessageFeedbackLoading', payload: { messageId: message.messageId, loading: true } });
    try {
      await sendMessageFeedback(currentChat?.chatId, message.messageId, true);
      dispatch({ type: 'chatMessage/setMessageFeedbackStatus', payload: { messageId: message.messageId, isPositive: true } });
      dispatch({ type: 'chatMessage/setMessageFeedbackError', payload: { messageId: message.messageId, error: null } });
    } catch (err) {
      console.log(`Exception while sending feedback: ${err}`);
      dispatch({ type: 'chatMessage/setMessageFeedbackError', payload: { messageId: message.messageId, error: 'Failed to send feedback' } });
    } finally {
      dispatch({ type: 'chatMessage/setMessageFeedbackLoading', payload: { messageId: message.messageId, loading: false } });
    }
  };

  const handleThumbsDownBotMessage = async () => {
    if (!currentChat?.chatId || !message.messageId) return;
    dispatch({ type: 'chatMessage/setMessageFeedbackLoading', payload: { messageId: message.messageId, loading: true } });
    try {
      await sendMessageFeedback(currentChat?.chatId, message.messageId, false);
      dispatch({ type: 'chatMessage/setMessageFeedbackStatus', payload: { messageId: message.messageId, isPositive: false } });
      dispatch({ type: 'chatMessage/setMessageFeedbackError', payload: { messageId: message.messageId, error: null } });
    } catch (err) {
      console.log(`Exception while sending feedback: ${err}`);
      dispatch({ type: 'chatMessage/setMessageFeedbackError', payload: { messageId: message.messageId, error: 'Failed to send feedback' } });
    } finally {
      dispatch({ type: 'chatMessage/setMessageFeedbackLoading', payload: { messageId: message.messageId, loading: false } });
    }
  };

  const handleRegenerateResponse = useCallback(async () => {
    if (!currentChat?.messages || index <= 0) return;

    let previousUserMessage: Message | null = null;
    for (let i = index - 1; i >= 0; i--) {
      const prevMessage = currentChat.messages[i];
      if (!prevMessage.isBot) {
        previousUserMessage = prevMessage;
        break;
      }
    }

    if (previousUserMessage?.text && previousUserMessage.messageId) {
      const chatId = currentChat.chatId ? String(currentChat.chatId) : "new-chat";

      dispatch(editLastUserMessage({
        chatId: chatId,
        messageId: previousUserMessage.messageId,
        text: previousUserMessage.text,
      }));

      setTimeout(() => {
        scrollToUserMessage();
      }, 100);
      await sendMessage(previousUserMessage.text, previousUserMessage.messageId);
    }
  }, [currentChat, index, sendMessage, scrollToUserMessage, dispatch]);

  // Wrapper for handleInterruptResponse that marks interrupt as processed
  const handleInterruptResponseWithProcessing = useCallback(async (responseType: string, responseData: any = {}, interruptId?: string, chatId?: string | number, messageId?: number) => {
    // Scroll to show the user message after a short delay to allow DOM update
    setTimeout(() => {
      scrollToUserMessage();
    }, 100);

    // Treat approve/cancel as user messages similar to answer/modify flows
    if (responseType === 'approve') {
      await respondWithUserMessage('approve', 'Approved', { interruptId, chatId, messageId });
      return;
    }
    if (responseType === 'cancel') {
      await respondWithUserMessage('cancel', 'Cancelled', { interruptId, chatId, messageId });
      return;
    }

    return handleInterruptResponse(responseType, responseData, interruptId, chatId, messageId);
  }, [handleInterruptResponse, respondWithUserMessage]);

  // Remove the old renderMessageContent function and just use the memoized one
  const memoizedRenderMessageContent = useCallback((message: Message, index: number, isLastBotMessage = false) => {
    if (!message.text && message.tags && message.tags.length === 0) return null;

    // Use tags from SSE handler - interrupts will be handled separately in main content
    const specialTags = (message.tags || []).filter(tag =>
      ['thinking', 'progress', 'transfer', 'research', 'summarization'].includes(tag.type)
      && tag.content && tag.content.trim()
    );

    // Memory-saved tags: show a compact "Updated saved memory" header above the answer
    const memorySavedTags = canViewMemoryTags
      ? (message.tags || []).filter(
        (tag) =>
          tag.type === "memory_saved" &&
          tag.text &&
          String(tag.text).trim().length > 0
      )
      : [];

    const memorySavedHeader = (
      <MemorySavedHeader
        memorySavedTags={memorySavedTags}
        onManageClick={() => setShowMemoryModal(true)}
      />
    );

    // Get SimulationDiff tags separately for display after answer completion
    const simulationDiffTags = (message.tags || []).filter(tag =>
      tag.type === 'SimulationDiff' && tag.content && tag.content.trim()
    );

    // Extract actionable interrupt from message.interrupts (exclude SimulationDiff)
    const actionableInterrupt = (message.isBot && Array.isArray(message.interrupts))
      ? [...message.interrupts].reverse().find((i: any) => i?.type !== 'SimulationDiff') || null
      : null;

    // Get interrupt tags separately for main content area - only for last bot message
    // and only if the last thinking token is an interrupt and hasn't been processed
    // IMPORTANT: If SimulationDiff is present, ignore interrupt tags per spec
    const interruptTags: any[] = [];
    if (!actionableInterrupt && simulationDiffTags.length === 0 && message.tags && message.tags.length > 0) {
      const lastTag = message.tags[message.tags.length - 1];
      if (lastTag.type === 'interrupt' && lastTag.content && lastTag.content.trim()) {
        try {
          const jsonMatch = lastTag.content.match(/<interrupt>([\s\S]*?)<\/interrupt>/);
          let jsonString = jsonMatch ? jsonMatch[1] : lastTag.content;
          const interruptIdMatch = jsonString.match(/'interrupt_id':\s*'([^']+)'/);
          if (interruptIdMatch) {
            interruptTags.push(lastTag);
          } else {
            interruptTags.push(lastTag);
          }
        } catch (error) {
          interruptTags.push(lastTag);
        }
      }
    }

    const hasSpecialTags = specialTags.length > 0;

    // Clean XML tags from message.text since tags are stored separately in message.tags
    let cleanedText = message.text ? message.text.replace(/<(thinking|progress|transfer|research|interrupt|simulation_diff)>[\s\S]*?<\/\1>/g, '').trim() : '';
    
    // For error tags, if text is empty, use the error tag content
    if (isEffectiveError && !cleanedText && errorTag?.content) {
      cleanedText = errorTag.content;
    }

    if (currentChat?.isStreaming) {
      cleanedText = cleanedText.replace(/<(thinking|progress|transfer|research|interrupt|simulation_diff)>[\s\S]*$/g, '').trim();
    }

    const formattedText = String(cleanedText).replace(/\\n/g, "  \n").replace(/\\/g, "  \\");

    const hasPMTags = /<pm:(card|status|progress|list|table)/.test(formattedText);

    const contentRenderer = <PMRenderer content={formattedText} isStreaming={currentChat?.isStreaming} />;

    // Compute simulation diff interrupt from interrupts array
    const simDiffInterrupt = Array.isArray(message.interrupts) ? message.interrupts.find((i: any) => i?.type === 'SimulationDiff') : null;

    if ((!hasSpecialTags && simulationDiffTags.length === 0 && (!Array.isArray(message.interrupts) || message.interrupts.length === 0))) {
      return (
        <div className="message-content">
          {memorySavedHeader}
          {contentRenderer}

          <MessageSimulationDiff
            simulationDiffTags={simulationDiffTags as any}
            interruptData={simDiffInterrupt ? simDiffInterrupt.data : null}
            isStreaming={currentChat?.isStreaming}
            visible={(simulationDiffTags.length > 0) || !!simDiffInterrupt}
            messageId={message.messageId}
            isLastMessage={isLastBotMessage}
          />
        </div>
      );
    }

    return (
      <div className="message-content">
        {memorySavedHeader}

        {hasSpecialTags && (
          <ChatMessageTimeline
            tags={specialTags}
            streaming={currentChat?.isStreaming}
            hasPMTags={hasPMTags}
            isLastMessage={isLastBotMessage}
            resetExpansion={resetExpansion}
          />
        )}

        {(!!actionableInterrupt || interruptTags.length > 0) && (
          <MessageInterrupt
            sseInterrupt={actionableInterrupt}
            interruptTags={interruptTags}
            isSSEConnected={isSSEConnected}
            interruptLoading={interruptLoading}
            showBigChatBox={showBigChatbox}
            onRespond={handleInterruptResponseWithProcessing}
            isLastBotMessage={isLastBotMessage}
          />
        )}

        {contentRenderer && (
          <div className="message-text mt-2">
            <div className="inline">
              {contentRenderer}
              {isLastBotMessage && currentChat?.isStreaming && message.text && (
                <span className="streaming-dots ml-1 text-lg">
                  <span className="dot animate-bounce text-gray-600 dark:text-gray-400 font-bold">.</span>
                  <span className="dot animate-bounce text-gray-600 dark:text-gray-400 font-bold" style={{ animationDelay: '0.2s' }}>.</span>
                  <span className="dot animate-bounce text-gray-600 dark:text-gray-400 font-bold" style={{ animationDelay: '0.4s' }}>.</span>
                </span>
              )}
            </div>
          </div>
        )}

        <MessageSimulationDiff
          simulationDiffTags={simulationDiffTags as any}
          interruptData={simDiffInterrupt ? simDiffInterrupt.data : null}
          isStreaming={currentChat?.isStreaming}
          visible={(simulationDiffTags.length > 0) || !!simDiffInterrupt}
          messageId={message.messageId}
          isLastMessage={isLastBotMessage}
        />
      </div>
    );
  }, [currentChat, canViewMemoryTags]);

  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;
    const emailAnchors = Array.from(root.querySelectorAll('a[href^="mailto:"]')) as HTMLAnchorElement[];
    emailAnchors.forEach(a => {
      try {
        if ((a as any).__pillified) {
          const wrap = a.parentElement as HTMLElement | null;
          if (wrap && wrap.classList.contains('research-pill-wrap')) {
            const tipTitleEl = wrap.querySelector('.pill-tooltip-title') as HTMLElement | null;
            const originalLabel = tipTitleEl?.textContent?.trim();
            a.classList.remove('research-pill');
            const innerFavicon = a.querySelector('img.w-3.h-3.rounded-full.inline-block') as HTMLImageElement | null;
            if (innerFavicon) innerFavicon.remove();
            if (originalLabel && originalLabel.length > 0) {
              a.textContent = originalLabel;
            }
            if (wrap.parentNode) {
              wrap.parentNode.insertBefore(a, wrap);
              wrap.remove();
            }
          }
          (a as any).__pillified = false;
        }
        a.classList.add('text-blue-600', 'dark:text-blue-400', 'hover:underline');
      } catch {
      }
    });

    const anchors = Array.from(root.querySelectorAll('a[href^="http"]')) as HTMLAnchorElement[];
    anchors.forEach(a => {
      try {
        const urlObj = new URL(a.href);
        const host = urlObj.hostname;
        // Skip pill styling for GitHub commit and pull request links so they render as normal markdown
        const path = urlObj.pathname || '';
        const pathSegments = path.split('/').filter(Boolean);
        const isGithub = /(^|\.)github\.com$/i.test(host);
        const isGithubCommitOrPR = isGithub && (pathSegments.includes('commit') || pathSegments.includes('pull'));
        if (isGithubCommitOrPR) {
          // For GitHub commit/PR links, do not apply pill styling at all.
          // If previously pillified, unwrap and restore original anchor content.
          if ((a as any).__pillified) {
            const wrap = a.parentElement as HTMLElement | null;
            if (wrap && wrap.classList.contains('research-pill-wrap')) {
              const tipTitleEl = wrap.querySelector('.pill-tooltip-title') as HTMLElement | null;
              const originalLabel = tipTitleEl?.textContent?.trim();
              a.classList.remove('research-pill');
              const innerFavicon = a.querySelector('img.w-3.h-3.rounded-full.inline-block') as HTMLImageElement | null;
              if (innerFavicon) innerFavicon.remove();
              if (originalLabel && originalLabel.length > 0) {
                a.textContent = originalLabel;
              }
              if (wrap.parentNode) {
                wrap.parentNode.insertBefore(a, wrap);
                wrap.remove();
              }
            }
            (a as any).__pillified = false;
          }
          a.classList.add('text-blue-600', 'dark:text-blue-400', 'hover:underline');
          return;
        }
        const insideTable = !!a.closest('.pm-table, table, .pm-table-wrapper');
        if (insideTable) {
          if ((a as any).__pillified) {
            const wrap = a.parentElement as HTMLElement | null;
            if (wrap && wrap.classList.contains('research-pill-wrap')) {
              const tipTitleEl = wrap.querySelector('.pill-tooltip-title') as HTMLElement | null;
              const originalLabel = tipTitleEl?.textContent?.trim();
              a.classList.remove('research-pill');
              const innerFavicon = a.querySelector('img.w-3.h-3.rounded-full.inline-block') as HTMLImageElement | null;
              if (innerFavicon) innerFavicon.remove();
              if (originalLabel && originalLabel.length > 0) {
                a.textContent = originalLabel;
              }
              if (wrap.parentNode) {
                wrap.parentNode.insertBefore(a, wrap);
                wrap.remove();
              }
            }
            (a as any).__pillified = false;
          }
          a.classList.add('text-blue-600', 'dark:text-blue-400', 'hover:underline');
          return;
        }
        const isInternalUrl = [
          "http://localhost:3000",
          "https://stage.cadencepro.ai",
          "https://qa.cadencepro.ai",
          "https://www.cadencepro.ai",
          "https://cadencepro.ai",
          "https://dev.azure.com/",
        ].some((prefix) => a.href.startsWith(prefix));

        if (isInternalUrl) {
     
          return;
        }
        
        return;
        if ((a as any).__pillified || a.classList.contains('research-pill') || a.closest('.research-pill-wrap')) return;
        const shortHost = host.replace(/^www\./, '');
        const originalText = a.getAttribute('title') || a.textContent || a.href;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(originalText || '')) {
          a.classList.add('text-blue-600', 'dark:text-blue-400', 'hover:underline');
          return;
        }
        a.classList.add('research-pill');
        // Wrap for tooltip
        const wrap = document.createElement('span');
        wrap.className = 'research-pill-wrap inline-pill-processed';
        a.parentNode?.insertBefore(wrap, a);
        wrap.appendChild(a);
        // favicon
        const img = document.createElement('img');
        img.className = 'w-3 h-3 rounded-full inline-block';
        img.src = `https://www.google.com/s2/favicons?domain=${host}&sz=64`;
        a.textContent = shortHost;
        a.insertBefore(img, a.firstChild);
        // tooltip
        const tip = document.createElement('span');
        tip.className = 'pill-tooltip';
        const tipFavicon = `https://www.google.com/s2/favicons?domain=${host}&sz=64`;
        tip.innerHTML = `
          <div class="pill-tooltip-card">
            <div class="pill-tooltip-header">
              <img class="pill-tooltip-favicon" src="${tipFavicon}" alt="" />
              <span class="pill-tooltip-domain">${host}</span>
            </div>
            <div class="pill-tooltip-title">${originalText}</div>
          </div>`;
        wrap.appendChild(tip);
        (a as any).__pillified = true;
      } catch {
        // ignore
      }
    });
  }, [message?.text, currentChat?.isStreaming]);

  const hasSimDiff = React.useMemo(() => {
    const simDiffInterrupt = Array.isArray(message.interrupts)
      ? message.interrupts.find((i: any) => i?.type === 'SimulationDiff')
      : null;
    const simulationDiffTags = (message.tags || []).filter(tag =>
      tag.type === 'SimulationDiff' && tag.content && tag.content.trim()
    );
    return (simulationDiffTags.length > 0) || !!simDiffInterrupt;
  }, [message.interrupts, message.tags]);

  return (
    <div
      data-bot-message-id={message.id || 'temp'}
      className={`flex items-start min-w-0 w-full max-w-full py-2 sm:py-3 pb-6 text-gray-400 text-xs sm:text-sm chat-message bot-message group 
        ${isEffectiveError ? 'border-l-4 border-red-400 dark:border-red-500 pl-2 bg-red-50 dark:bg-red-900/10 ml-2' : ''}
      `}
    >
      <div className="mr-1.5 sm:mr-2 -mt-0.5 h-6 w-6 sm:h-8 sm:w-8 flex-shrink-0 relative">
        {isEffectiveError ? (
          <div className="h-6 w-8 sm:h-8 sm:w-8 rounded-full border border-red-400 dark:border-red-500 bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
            <svg className="h-3 w-3 sm:h-4 sm:w-4 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
        ) : (
          <img
            src="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/cadencelogo.webp"
            className={`h-6 w-6 sm:h-7 sm:w-7 rounded-full border border-gray-200 dark:border-gray-600 transition-all duration-300 ${isLastBotMessage && shouldSpinAvatar ? 'animate-spin-slow' : ''}`}
            alt="Cadence Logo"
          />
        )}

      </div>
      <div className="min-w-0 flex-1 relative">
        {message.isBot && !message.text && message.tags.length === 0 && (
          <div className=" flex items-center justify-start">
            <HiOutlineLightBulb className="mr-2" style={{ fontSize: 12 }} />
            <span className="thinking-text  thinking-text-glow mt-0.5" style={{ fontSize: 12, fontWeight: 100 }}>Thinking</span>
            <div className="thinking-dots">
              <div className="thinking-dot"></div>
              <div className="thinking-dot"></div>
              <div className="thinking-dot"></div>
            </div>
          </div>
        )}
        <div
          ref={contentRef}
          className={`text-[13px] sm:text-[15px] font-normal font-['Inter'] leading-relaxed 
            ${isEffectiveError ? 'text-red-800 dark:text-red-200' : 'text-gray-800 dark:text-gray-200'}
          `}
        >
          {memoizedRenderMessageContent(message, index, isLastBotMessage)}
        </div>

        <SelectionAddToChat
          containerRef={contentRef as React.RefObject<HTMLElement>}
          canAdd={!!canAddSelection}
          onAdd={(text: string) => handleAddSelectionToInput(text)}
          allowedSelector={'.message-text'}
        />

        {/* Copy button - appears on hover, only show if message has content and is not currently streaming and is not an error */}
        {hasContent && (!currentChat?.isStreaming || !isLastBotMessage) && !isEffectiveError && (
          <div className="flex  flex-row items-center gap-2 absolute -bottom-10 mb-4  left-1">
            <button
              onClick={handleCopyBotMessage}
              className="copy-button w-7 h-7  rounded-md shadow-sm  flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-600"
              title="Copy message"
            >
              <HiOutlineDocumentDuplicate className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-300" />
            </button>

            {canAddSelection && (
              <>
                <button
                  onClick={handleThumbsUpBotMessage}
                  disabled={feedbackLoading}
                  className={`w-7 h-7  text-gray-600 dark:text-gray-300  rounded-md shadow-sm flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-600 border border-transparent ${feedbackLoading ? '' : ''}`}
                  title="Good response"
                >
                  <HiOutlineThumbUp className={`w-4 h-4 sm:w-5 sm:h-5 ${feedbackStatus === true ? 'fill-current' : ''}`} />
                </button>
                <button
                  onClick={handleThumbsDownBotMessage}
                  disabled={feedbackLoading}
                  className={`w-7 h-7  text-gray-600 dark:text-gray-300 rounded-md shadow-sm flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-600 border border-transparent ${feedbackLoading ? '' : ''}`}
                  title="Bad response"
                >
                  <HiOutlineThumbDown className={`w-4 h-4 sm:w-5 sm:h-5 ${feedbackStatus === false ? 'fill-current' : ''}`} />
                </button>
                {!hasSimDiff && (
                  <button
                    onClick={handleRegenerateResponse}
                    disabled={currentChat?.isLoading || currentChat?.isStreaming}
                    className={`w-7 h-7  text-gray-600 dark:text-gray-300 rounded-md shadow-sm flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-600 border border-transparent ${currentChat?.isLoading || currentChat?.isStreaming ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title="Regenerate response"
                  >
                    <HiArrowPath className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                )}
              </>
            )}

            {sourceIcons.length > 0 && (
              <button
                onClick={updateResearchLinks}
                className="hidden px-3   p-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-full  sm:flex items-center gap-1   cursor-pointer"
              >
                <div className="flex -space-x-1">
                  {sourceIcons.map((src, i) => (
                    <img
                      key={`${src}-${i}`}
                      src={src}
                      alt=""
                      className="w-5 h-5 rounded-full ring-1 ring-gray-200 dark:ring-gray-700 bg-white"
                    />
                  ))}
                </div>
                <span className="text-gray-600 dark:text-gray-300 mx-1">Sources</span>
              </button>
            )}
          </div>
        )}
      </div>

      <UserMemoryManageModal
        isOpen={showMemoryModal}
        onClose={() => setShowMemoryModal(false)}
      />
    </div>
  );
};

export default MemoizedBotMessage;